/**
 * Тема оформления.
 *
 * По умолчанию приложение следует настройке системы — это правильно: человек
 * уже сказал ей, как ему удобно. Но системная настройка одна на всё устройство,
 * а привычки разные: кто-то держит систему в тёмной теме, а списки желаний
 * предпочитает светлыми. Поэтому выбор можно переопределить вручную.
 *
 * Выбор хранится локально, а не на сервере: он про устройство, а не про
 * человека. На телефоне в постели и за рабочим столом удобны разные темы,
 * и синхронизировать их между устройствами было бы вредно.
 */

export type ThemeChoice = 'system' | 'light' | 'dark';

const STORAGE_KEY = 'wishare:theme';

export function readThemeChoice(): ThemeChoice {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === 'light' || stored === 'dark' || stored === 'system') return stored;
  } catch {
    /* приватный режим без хранилища — просто следуем системе */
  }
  return 'system';
}

/** Отписка от слежения за системной темой, если оно было установлено. */
let unwatchSystem: (() => void) | null = null;

/**
 * Применяет выбор к странице.
 *
 * Атрибут всегда содержит ту тему, которая показана на самом деле, — так
 * палитра в стилях описана один раз, без второй копии в медиазапросе.
 *
 * Чтобы «как в системе» продолжало означать именно это, при таком выборе
 * подписываемся на смену системной настройки: человек с автопереключением
 * по закату должен увидеть смену темы сразу, а не после перезагрузки страницы.
 */
export function applyThemeChoice(choice: ThemeChoice): void {
  // Прежняя подписка больше не нужна: иначе выбор «светлая» будет каждый
  // вечер перебиваться системой.
  unwatchSystem?.();
  unwatchSystem = null;

  paint(effectiveTheme(choice));

  if (choice !== 'system') return;

  try {
    const query = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = (): void => paint(query.matches ? 'dark' : 'light');
    query.addEventListener('change', onChange);
    unwatchSystem = () => query.removeEventListener('change', onChange);
  } catch {
    /* браузер не умеет следить — останемся на теме, вычисленной при загрузке */
  }
}

function paint(theme: 'light' | 'dark'): void {
  document.documentElement.setAttribute('data-theme', theme);

  // Цвет адресной строки на телефоне должен совпадать с фоном приложения,
  // иначе сверху остаётся полоса чужого цвета.
  for (const meta of document.querySelectorAll('meta[name="theme-color"]')) {
    meta.setAttribute('content', theme === 'dark' ? '#0e0b16' : '#f7f5fc');
    meta.removeAttribute('media');
  }
}

export function saveThemeChoice(choice: ThemeChoice): void {
  try {
    if (choice === 'system') localStorage.removeItem(STORAGE_KEY);
    else localStorage.setItem(STORAGE_KEY, choice);
  } catch {
    /* не сохранилось — тема продержится до перезагрузки, это не беда */
  }
  applyThemeChoice(choice);
}

export function prefersDark(): boolean {
  try {
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  } catch {
    return true; // тёмная — основная тема продукта
  }
}

/** Какая тема показана сейчас, с учётом системной настройки. */
export function effectiveTheme(choice: ThemeChoice): 'light' | 'dark' {
  if (choice === 'light' || choice === 'dark') return choice;
  return prefersDark() ? 'dark' : 'light';
}

export const THEME_LABEL: Record<ThemeChoice, string> = {
  system: 'Как в системе',
  light: 'Светлая',
  dark: 'Тёмная',
};
